/**
 * rs-product-core.js
 *
 * Centralized PDP infrastructure for BaubleBar on Broadcast.
 *  - ATC Gate Manager  — gate-based Add to Cart button state
 *  - Line Properties    — hidden cart property inputs
 *  - Multi-Item Submit — intercepts cart add for bundled payloads
 *  - Section Swap      — outerHTML swap for custom variant-aware blocks
 *  - Alt-text Gallery  — variant-filtered slide rendering (variant:Val1|Val2|Val3)
 *  - Siblings Dropdown — navigate on sibling color/size dropdown change
 *  - RS Modal          — centralized <dialog class="rs-modal"> open/close
 *
 * Broadcast owns the cart flow; this file only prepares data.
 */
(function () {
  'use strict';

  if (window.RSProduct) return;

  let _config = { sectionId: null, enableZoom: false };
  let _sectionId = null;
  let _defaultText = '';
  let _defaultDisabled = false;
  let _initialized = false;

  // ═══════════════════════════════════════════
  //  Helper Functions
  // ═══════════════════════════════════════════
  function _findElement(id) {
    return document.getElementById(id + '-' + _sectionId);
  }

  function _findWrapper() {
    return document.getElementById('MainProduct--' + _sectionId);
  }

  function _findButton() {
    return document.getElementById('ProductSubmitButton-' + _sectionId);
  }

  function _findForm() {
    const wrapper = _findWrapper();
    return wrapper ? wrapper.querySelector('form[data-product-form]') : null;
  }

  function _findCartItems() {
    return document.querySelector('cart-items');
  }

  // ═══════════════════════════════════════════
  //  ATC Gate Manager
  // ═══════════════════════════════════════════
  const _gates = new Map();

  // Capture the button's current disabled state and visible text label
  function _captureFromButton(button) {
    if (!button) return;
    const textElement = button.querySelector('[data-add-to-cart-text]');
    if (textElement) {
      let text = '';
      textElement.childNodes.forEach(function (node) {
        if (node.nodeType === 3) text += node.textContent;
      });
      const cleaned = text.trim();
      if (cleaned) _defaultText = cleaned;
    }
    _defaultDisabled = button.hasAttribute('disabled');
  }

  function _captureDefaults() {
    _captureFromButton(_findButton());
  }

  // Keep data-preorder in sync so Broadcast's updatePreorderState reads the right value
  function _syncButtonAttributes(html) {
    if (!html) return;
    const freshButton = html.getElementById('ProductSubmitButton-' + _sectionId);
    const domButton = _findButton();
    if (!freshButton || !domButton) return;
    domButton.dataset.preorder = freshButton.dataset.preorder || 'false';
  }

  // Write the final disabled state and text to the ATC button while preserving
  function _applyButtonState(disabled, text) {
    const button = _findButton();
    if (!button) return;
    button.toggleAttribute('disabled', disabled);
    const textElement = button.querySelector('[data-add-to-cart-text]');
    if (!textElement) return;

    const existingPrice = textElement.querySelector('[data-product-price]');
    if (existingPrice) {
      const priceHTML = existingPrice.outerHTML;
      textElement.textContent = text;
      textElement.insertAdjacentHTML('beforeend', ' ' + priceHTML);
    } else {
      textElement.textContent = text;
    }
  }

  // Compute the final button state by layering active gates over the defaults and write it to the DOM. 
  // Gates are skipped when the theme has explicitly disabled the button with a non-default label (sold out, unavailable, etc.).
  function _resolveButton() {
    if (!_findButton()) return;

    const atcString = window.theme?.strings?.addToCart;
    const themeBlocks = _defaultDisabled && atcString && _defaultText !== atcString;

    let disabled = _defaultDisabled;
    let text = _defaultText;

    if (!themeBlocks) {
      let topPriority = -1;
      _gates.forEach(function (gate) {
        if (gate.disabled) {
          disabled = true;
          if (gate.priority > topPriority) {
            topPriority = gate.priority;
            text = gate.text;
          }
        }
      });
    }

    _applyButtonState(disabled, text);
  }

  let _resolveScheduled = false;

  // Coalesce multiple gate/default mutations within the same tick into a single
  function _scheduleResolve() {
    if (_resolveScheduled) return;
    _resolveScheduled = true;
    queueMicrotask(function () {
      _resolveScheduled = false;
      _resolveButton();
    });
  }

  // Prevent third-party scripts (e.g. uploadly) from permanently enabling the
  // button while a gate is active. If the disabled attribute is removed by
  // anything other than our own _applyButtonState, re-run the gate resolution.
  function _guardButtonDisabled() {
    const button = _findButton();
    if (!button) return;
    new MutationObserver(function () {
      const anyGateActive = Array.from(_gates.values()).some(function (g) { return g.disabled; });
      if (anyGateActive && !button.hasAttribute('disabled')) {
        _scheduleResolve();
      }
    }).observe(button, { attributes: true, attributeFilter: ['disabled'] });
  }

  function _patchProductForm() {
    const wrapper = _findWrapper();
    if (!wrapper) return;
    const form = wrapper.querySelector('product-form');
    if (!form || form._rsPatched) return;

    if (typeof form.toggleSubmitButton !== 'function') {
      if (window.customElements?.whenDefined) {
        window.customElements.whenDefined('product-form').then(_patchProductForm);
      }
      return;
    }

    form._rsPatched = true;
    form.toggleSubmitButton = function (disable, text) {
      _defaultDisabled = !!disable;
      if (text !== undefined) {
        _defaultText = text;
      } else if (window.theme?.strings?.addToCart) {
        _defaultText = window.theme.strings.addToCart;
      }
      if (_initialized) _scheduleResolve();
    };
  }

  // ═══════════════════════════════════════════
  //  Line Item Properties
  // ═══════════════════════════════════════════
  const _properties = new Map();

  function _syncInput(key) {
    const form = _findForm();
    if (!form) return;
    const inputName = 'properties[' + key + ']';
    let input = form.querySelector('input[name="' + inputName + '"][data-rs-prop]');
    const value = _properties.get(key);

    if (value == null) { if (input) input.remove(); return; }

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      input.setAttribute('data-rs-prop', key);
      form.appendChild(input);
    }
    input.value = String(value);
  }

  // ═══════════════════════════════════════════
  //  Multi-Item Submit
  // ═══════════════════════════════════════════
  let _itemsBuilder = null;
  let _itemsBuilderId = null;
  let _interceptBound = false;

  // Intercept the theme:cart:add event for this section and, 
  // when a builder is registered, delegate to it to produce a multi-item payload
  function _onCartAdd(event) {
    if (!_itemsBuilder) return;
    const button = event.detail?.button;
    if (!button) return;

    const form = button.form || button.closest('form');
    if (!form) return;

    const mainForm = _findForm();
    if (form !== mainForm) return;

    event.stopImmediatePropagation();

    if (button.hasAttribute('disabled')) return;
    if (!form.checkValidity()) { form.reportValidity(); return; }

    const cartItems = _findCartItems();
    if (!cartItems || typeof cartItems.addToCart !== 'function') return;

    Promise.resolve(_itemsBuilder(button))
      .then(function (items) {
        if (Array.isArray(items) && items.length) cartItems.addToCart(items, button);
      })
      .catch(function (error) { console.error('[RSProduct]', error); });
  }

  function _bindCartIntercept() {
    if (_interceptBound) return;
    document.addEventListener('theme:cart:add', _onCartAdd, true);
    _interceptBound = true;
  }

  // ═══════════════════════════════════════════
  //  Gallery Injection
  // ═══════════════════════════════════════════
  const _injectedSlides = new Map();
  let _injectedCounter = 0;

  function _findGallerySlider() {
    const gallery = _findElement('RsProductGallery');
    return gallery ? gallery.querySelector('[data-product-media-list]') : null;
  }

  function _findThumbsHolder() {
    const gallery = _findElement('RsProductGallery');
    return gallery ? gallery.querySelector('[data-thumbs-slider]') : null;
  }

  function _buildSlideElement(options) {
    const alt = options.alt || 'Customizer Image';

    let zoomAttrs = '';
    if (_config.enableZoom) {
      zoomAttrs = ` data-zoom-image data-image-src="${options.src}" data-image-width="1110" data-image-height="880"`;
    }

    const customizerView = _config.customizerTemplate ? `<div class="customizer-view"></div>` : '';

    const template = document.createElement('template');
    template.innerHTML = `
      <div
        class="product__slide media--hidden"
        data-image-id="${options._id}"
        data-media-id="${_sectionId}-${options._id}"
        data-type="image"
        data-slide
        data-customizer-slide
        data-rs-injected="${options._id}"
      >
        <div
          class="product__photo"
          tabindex="0"
          ${zoomAttrs}
        >
          <figure class="image-wrapper image-wrapper--cover lazy-image lazy-image--backfill">
            <img src="${options.src}" alt="${alt}" loading="lazy">
          </figure>
          ${customizerView}
        </div>
      </div>
    `;
    return template.content.firstElementChild;
  }

  function _buildThumbElement(options) {
    const thumbSrc = options.src;

    const template = document.createElement('template');
    template.innerHTML = `
      <div class="product__thumb" data-thumb-item data-rs-injected="${options._id}">
        <a
          class="product__thumb__link"
          href="${options.src}"
          aria-current="false"
          data-media-id="${_sectionId}-${options._id}"
          data-thumb-link
        >
          <figure class="image-wrapper lazy-image lazy-image--backfill" style="--aspect-ratio: 1;">
            <img src="${thumbSrc}" alt="Customizer Thumbnail" loading="lazy" width="150" height="150" class="fit-cover">
          </figure>
        </a>
      </div>
    `;
    return template.content.firstElementChild;
  }

  function _insertAtIndex(parent, element, index) {
    // Count only visible, non-injected product slides so the index is relative
    // to what the user actually sees — inject(src, 1) lands after the 1st visible image.
    const children = parent.querySelectorAll('[data-slide]:not([data-rs-injected]):not(.rs-alt-gallery--hidden)');
    if (index == null || index >= children.length) {
      parent.appendChild(element);
    } else {
      const ref = children[Math.max(0, index)];
      parent.insertBefore(element, ref);
    }
  }

  function _insertThumbAtIndex(parent, element, index) {
    const children = parent.querySelectorAll('[data-thumb-item]:not([data-rs-injected]):not(.rs-alt-gallery--hidden)');
    if (index == null || index >= children.length) {
      parent.appendChild(element);
    } else {
      const ref = children[Math.max(0, index)];
      parent.insertBefore(element, ref);
    }
  }

  // ═══════════════════════════════════════════
  //  Section Element Swap
  // ═══════════════════════════════════════════
  function _swapElement(name, html) {
    const nextElement = html.getElementById(name + '-' + _sectionId);
    if (!nextElement) return;
    const currentElement = document.getElementById(name + '-' + _sectionId);
    if (currentElement) currentElement.outerHTML = nextElement.outerHTML;
  }

  // Replace every element tagged with data-rs-swap="..." inside the wrapper.
  // Matched positionally (source[0]→target[0]) so multiple elements with the
  // same data-rs-swap name (e.g. main product + gift box upsell) don't clobber each other.
  function _swapAllElementsWithAttributes(html) {
    const wrapper = _findWrapper();
    if (!wrapper) return;

    const sourcesByName = {};
    html.querySelectorAll('[data-rs-swap]').forEach(function (source) {
      const name = source.getAttribute('data-rs-swap');
      if (!sourcesByName[name]) sourcesByName[name] = [];
      sourcesByName[name].push(source);
    });

    Object.keys(sourcesByName).forEach(function (name) {
      const sources = sourcesByName[name];
      const targets = Array.from(wrapper.querySelectorAll('[data-rs-swap="' + name + '"]'));
      sources.forEach(function (source, i) {
        const el = targets[i];
        if (!el) return;
        if (name === 'RsProductMtoInput') {
          _swapMtoInput(el, source);
        } else {
          el.outerHTML = source.outerHTML;
        }
      });
    });
  }

  // MTO input: only full-swap when config changes (variant has different limits).
  // When config is identical, preserve user-typed text and toggle visibility.
  function _swapMtoInput(current, incoming) {
    const currentConfig = current.querySelector('[data-mto-config]');
    const incomingConfig = incoming.querySelector('[data-mto-config]');
    const currentJson = currentConfig ? currentConfig.textContent.trim() : '';
    const incomingJson = incomingConfig ? incomingConfig.textContent.trim() : '';

    if (currentJson !== incomingJson) {
      current.outerHTML = incoming.outerHTML;
      return;
    }

    // Config unchanged — just sync visibility class
    current.classList.toggle('hidden', incoming.classList.contains('hidden'));
  }

  // ═══════════════════════════════════════════
  //  Alt-text Gallery
  // ═══════════════════════════════════════════
  let _galleryMediaData      = null;
  let _galleryMediaPositions = [];

  function _escAttr(str) {
    return String(str || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  function _initGalleryData() {
    const dataEl = document.getElementById('rs-gallery-data-' + _sectionId);
    if (!dataEl) return;
    try { _galleryMediaData = JSON.parse(dataEl.textContent); } catch { _galleryMediaData = null; }

    const posEl = document.getElementById('rs-gallery-media-positions-' + _sectionId);
    if (!posEl) return;
    try {
      _galleryMediaPositions = JSON.parse(posEl.textContent);
      const mediaList = _findGallerySlider();
      if (mediaList) {
        _galleryMediaPositions.forEach(function (entry) {
          const slide = mediaList.querySelector('[data-image-id="' + entry.id + '"]');
          if (slide) slide.setAttribute('data-media-position', entry.position);
        });
      }
    } catch { /* non-fatal */ }
  }

  // product-thumbs.setActiveThumb only fires via MutationObserver — needs an explicit
  // theme:media:select trigger after every outerHTML swap to restore is-active.
  function _activateGalleryThumb() {
    const gallery  = _findElement('RsProductGallery');
    const activeId = gallery && gallery.getAttribute('data-active-media');
    if (gallery && activeId) {
      gallery.dispatchEvent(new CustomEvent('theme:media:select', { detail: { id: activeId } }));
    }
  }

  function _parseGalleryAlt(alt) {
    const lower = (alt || '').toLowerCase().trim();
    if (!lower.startsWith('variant:')) return null;
    return lower.slice('variant:'.length).split('|').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function _buildGallerySlide(item, active) {
    const mediaId        = _sectionId + '-' + item.id;
    const aspectRatio    = (item.w / item.h).toFixed(4);
    const activeClass    = active ? 'media--active' : 'media--hidden';
    const zoomAttributes = _config.enableZoom
      ? ' data-zoom-image data-image-src="' + _escAttr(item.url) + '" data-image-width="' + item.w + '" data-image-height="' + item.h + '"'
      : '';

    const slideTemplate = document.createElement('template');
    slideTemplate.innerHTML = `<div class="product__slide ${activeClass}" data-rs-gallery-slide data-media-position="${item.position}" data-image-id="${item.id}" data-media-id="${mediaId}" data-type="image" data-slide><div class="product__photo" tabindex="0" style="--aspect-ratio:${aspectRatio}"${zoomAttributes}><figure class="image-wrapper image-wrapper--cover lazy-image lazy-image--backfill is-loading"><img src="${_escAttr(item.url)}" alt="${_escAttr(item.alt)}" width="${item.w}" height="${item.h}" loading="lazy" sizes="(min-width:750px) 50vw,100vw" class="is-loading"></figure></div></div>`;
    const slide = slideTemplate.content.firstElementChild;
    const img   = slide.querySelector('img');
    const fig   = slide.querySelector('.lazy-image');
    if (img && fig) {
      img.addEventListener('load', function () {
        fig.classList.remove('is-loading');
        img.classList.remove('is-loading');
      }, { once: true });
    }
    return slide;
  }

  // Must be called BEFORE _reinitGallery so connectedCallback fires with all correct slides in the DOM.
  function _renderGalleryForVariant(variantOptions) {
    if (!_galleryMediaData) return;

    const gallery   = _findElement('RsProductGallery');
    const mediaList = gallery && gallery.querySelector('[data-product-media-list]');
    if (!mediaList) return;

    // Re-tag non-image slides — _swapElement replaces server HTML and strips data-media-position.
    _galleryMediaPositions.forEach(function (entry) {
      const slide = mediaList.querySelector('[data-image-id="' + entry.id + '"]');
      if (slide) slide.setAttribute('data-media-position', entry.position);
    });

    const options   = variantOptions.map(function (o) { return o.toLowerCase().trim(); });
    const sectionEl = gallery.closest('[data-section-id]');

    // Universal (no prefix): show. "variant:" with no value: exclude (blank tag).
    // 1-part "variant:Val": show if the value appears anywhere in variant options.
    // 2+ part "variant:Val1|Val2": positional match — altParts[i] must equal options[i].
    //   This prevents "variant:Amethyst|Amethyst" matching "Amethyst / Aquamarine"
    //   when options share a value at a different position.
    var visible = _galleryMediaData.filter(function (item) {
      const altParts = _parseGalleryAlt(item.alt);
      if (altParts === null) return true;
      if (altParts.length === 0) return false;
      if (altParts.length === 1) return options.indexOf(altParts[0]) !== -1;
      return altParts.every(function (part, i) { return options[i] === part; });
    });
    if (visible.length === 0) visible = _galleryMediaData;

    // Remove non-matching videos from DOM (not CSS-hide) — hidden siblings still
    // receive Broadcast's nextElementSibling navigation, causing a blank gallery.
    mediaList.querySelectorAll('[data-type="image"][data-slide]:not([data-rs-injected])').forEach(function (s) { s.remove(); });

    mediaList.querySelectorAll('[data-type="video"][data-slide],[data-type="external_video"][data-slide]').forEach(function (videoSlide) {
      const videoParts = _parseGalleryAlt(videoSlide.querySelector('img')?.alt);
      const videoVisible = videoParts === null || (
        videoParts.length > 0 &&
        (videoParts.length === 1
          ? options.indexOf(videoParts[0]) !== -1
          : videoParts.every(function (p, i) { return options[i] === p; }))
      );
      if (!videoVisible) videoSlide.remove();
    });

    // Activate the first variant-specific image; fall back to index 0 when none exist.
    const firstVariantIdx = visible.findIndex(function (item) { return _parseGalleryAlt(item.alt) !== null; });
    const activeIdx       = firstVariantIdx >= 0 ? firstVariantIdx : 0;

    // Merge images with non-image slides in product.media order so nextElementSibling
    // navigation is correct for arrow prev/next.
    const imageSlides = visible.map(function (item, idx) { return _buildGallerySlide(item, idx === activeIdx); });
    const nonImageSlides = Array.from(mediaList.querySelectorAll('[data-slide][data-media-position]'))
      .sort(function (a, b) { return parseInt(a.getAttribute('data-media-position')) - parseInt(b.getAttribute('data-media-position')); });

    const mergedFragment = document.createDocumentFragment();
    var imageIndex = 0;
    nonImageSlides.forEach(function (nonImageSlide) {
      const nonImagePos = parseInt(nonImageSlide.getAttribute('data-media-position'));
      while (imageIndex < visible.length && visible[imageIndex].position < nonImagePos) {
        mergedFragment.appendChild(imageSlides[imageIndex]);
        imageIndex++;
      }
      mergedFragment.appendChild(nonImageSlide);
    });
    while (imageIndex < imageSlides.length) {
      mergedFragment.appendChild(imageSlides[imageIndex]);
      imageIndex++;
    }
    mediaList.appendChild(mergedFragment);

    // Must be set before _reinitGallery serialises the element — product-thumbs reads it in connectedCallback.
    if (visible.length > 0) {
      gallery.setAttribute('data-active-media', _sectionId + '-' + visible[activeIdx].id);
    }

    // Rebuild pswp thumbs to match visible slides — prevents count mismatch with Photoswipe items.
    if (_config.enableZoom) {
      const pswpTemplate = gallery.querySelector('[data-pswp-thumbs-template]');
      if (pswpTemplate) {
        while (pswpTemplate.content.firstChild) pswpTemplate.content.removeChild(pswpTemplate.content.firstChild);
        const thumbTemplate = document.createElement('template');
        visible.forEach(function (item) {
          thumbTemplate.innerHTML = `<figure class="pswp__thumb image-wrapper lazy-image" style="--aspect-ratio:1;"><img src="${_escAttr(item.preview || item.url)}" width="160" height="160" loading="lazy" class="fit-cover"></figure>`;
          pswpTemplate.content.appendChild(thumbTemplate.content.firstElementChild);
        });
      }
    }

    // Hide thumbnails whose slide doesn't exist (filtered image) or is hidden (filtered video).
    if (sectionEl) {
      sectionEl.querySelectorAll('[data-thumb-item]').forEach(function (thumb) {
        const thumbLink = thumb.querySelector('[data-media-id]');
        const mediaId   = thumbLink ? thumbLink.getAttribute('data-media-id') : null;
        if (!mediaId) return;
        const slide = mediaList.querySelector(`[data-media-id="${mediaId}"]`);
        thumb.classList.toggle('rs-alt-gallery--hidden', !slide || slide.classList.contains('rs-alt-gallery--hidden'));
      });
    }
  }

  function _reinitGallery() {
    const galleryElement = _findElement('RsProductGallery');
    if (!galleryElement) return;

    const sectionElement = galleryElement.closest('[data-section-id]');
    const productImagesElement = galleryElement.closest('product-images');
    if (!productImagesElement) return;

    // Sync pswp thumbs before replacement (template content is serialized with outerHTML)
    if (_config.enableZoom) {
      const pswpTemplate = productImagesElement.querySelector('[data-pswp-thumbs-template]');
      if (pswpTemplate) {
        pswpTemplate.content.querySelectorAll('[data-rs-pswp-thumb]').forEach(function (element) { element.remove(); });
        const allSlides = galleryElement.querySelectorAll('[data-slide]');

        _injectedSlides.forEach(function (entry, id) {
          if (!entry.slide || !entry.slide.isConnected) return;
          const imageSrc = entry.slide.querySelector('img')?.src;
          if (!imageSrc) return;

          const fragment = document.createElement('template');
          fragment.innerHTML = `<figure class="pswp__thumb image-wrapper lazy-image" style="--aspect-ratio: 1;" data-rs-pswp-thumb="${id}"><img src="${imageSrc}" width="160" height="160" loading="lazy" class="fit-cover"></figure>`;

          const slideIndex = Array.from(allSlides).indexOf(entry.slide);
          const templateChildren = pswpTemplate.content.children;
          if (slideIndex >= 0 && slideIndex < templateChildren.length) {
            pswpTemplate.content.insertBefore(fragment.content.firstElementChild, templateChildren[slideIndex]);
          } else {
            pswpTemplate.content.appendChild(fragment.content.firstElementChild);
          }
        });
      }
    }

    // outerHTML self-replacement — forces fresh constructor + connectedCallback on all gallery custom elements
    productImagesElement.outerHTML = productImagesElement.outerHTML;

    // Refresh injected slide references (old DOM nodes are destroyed by replacement)
    _injectedSlides.forEach(function (entry, id) {
      entry.slide = sectionElement.querySelector('[data-rs-injected="' + id + '"][data-slide]');
      entry.thumb = sectionElement.querySelector('[data-rs-injected="' + id + '"][data-thumb-item]');
    });

    _ensureCustomizerButton();
  }

  // ═══════════════════════════════════════════
  //  Customizer Button
  // ═══════════════════════════════════════════
  let _customizerButtonObserver = null;
  let _customizerScrollObserver = null;

  function _createCustomizerButton() {
    const template = document.createElement('template');
    template.innerHTML = `<button data-customizer-button class="customizer-button">CUSTOMIZER</button>`;
    const button = template.content.firstElementChild;
    button.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const firstInjectedId = _injectedSlides.keys().next().value;
      if (firstInjectedId) {
        window.RSProduct.gallery.navigate(firstInjectedId);
      }
    });
    return button;
  }

  function _isOnInjectedSlide(productImagesEl) {
    const activeMediaId = productImagesEl.getAttribute('data-active-media');
    if (!activeMediaId) return false;
    let onInjected = false;
    _injectedSlides.forEach(function (entry) {
      if (entry.slide && entry.slide.getAttribute('data-media-id') === activeMediaId) {
        onInjected = true;
      }
    });
    return onInjected;
  }

  function _updateCustomizerButtonVisibility() {
    const gallery = _findElement('RsProductGallery');
    if (!gallery) return;

    const productImagesEl = gallery.closest('product-images');
    if (!productImagesEl) return;

    const button = productImagesEl.querySelector('[data-customizer-button]');
    if (!button) return;

    const onInjected = _isOnInjectedSlide(productImagesEl);
    if (onInjected) {
      button.classList.remove('is-visible');
    } else {
      button.classList.add('is-visible');
    }
  }

  // Mobile: product gallery is a flex-scroll
  // Use IntersectionObserver instead to show/hide the button as the injected slide scrolls in and out of view.
  function _observeInjectedSlidesForScroll() {
    if (_customizerScrollObserver) {
      _customizerScrollObserver.disconnect();
      _customizerScrollObserver = null;
    }

    if (window.innerWidth > 749) return;
    if (_injectedSlides.size === 0) return;

    const gallery = _findElement('RsProductGallery');
    if (!gallery) return;

    const productImagesEl = gallery.closest('product-images');
    if (!productImagesEl) return;

    const visibleInjected = new Set();

    _customizerScrollObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          visibleInjected.add(entry.target);
        } else {
          visibleInjected.delete(entry.target);
        }
      });

      const button = productImagesEl.querySelector('[data-customizer-button]');
      if (!button) return;
      button.classList.toggle('is-visible', visibleInjected.size === 0);
    }, { threshold: 0.5 });

    _injectedSlides.forEach(function (entry) {
      if (entry.slide && entry.slide.isConnected) {
        _customizerScrollObserver.observe(entry.slide);
      }
    });
  }

  function _ensureCustomizerButton() {
    if (!_config.customizerTemplate) return;
    if (_injectedSlides.size === 0) return;

    const gallery = _findElement('RsProductGallery');
    if (!gallery) return;

    const productImagesEl = gallery.closest('product-images');
    if (!productImagesEl) return;

    // Remove stale button if present
    const existing = productImagesEl.querySelector('[data-customizer-button]');
    if (existing) existing.remove();

    // Insert new button
    const button = _createCustomizerButton();
    productImagesEl.appendChild(button);

    // Set initial visibility
    _updateCustomizerButtonVisibility();

    // Desktop: observe data-active-media attribute changes driven by the slider
    if (_customizerButtonObserver) _customizerButtonObserver.disconnect();
    _customizerButtonObserver = new MutationObserver(_updateCustomizerButtonVisibility);
    _customizerButtonObserver.observe(productImagesEl, { attributes: true, attributeFilter: ['data-active-media'] });

    // Mobile: flex-scroll has no slider — use IntersectionObserver instead
    _observeInjectedSlidesForScroll();
  }

  // ═══════════════════════════════════════════
  //  Variant Change
  // ═══════════════════════════════════════════
  function _subscribeVariantChange() {
    if (typeof window.subscribe !== 'function') return;

    window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function (event) {
      if (!event?.data) return;
      if (event.data.sectionId && event.data.sectionId !== _sectionId) return;

      const html = event.data.html;
      _syncButtonAttributes(html);
      _swapElement('RsProductGallery', html);
      _swapAllElementsWithAttributes(html);

      // Render variant-correct slides BEFORE _reinitGallery so its outerHTML swap
      // fires connectedCallback with the complete correct slide set in one shot —
      // no extra rAF, no extra outerHTML swap, no race condition.
      if (event.data.variant && event.data.variant.options) {
        _renderGalleryForVariant(event.data.variant.options);
      }

      _reinitGallery();
      _activateGalleryThumb();
      _scheduleResolve();
    });
  }

  // ═══════════════════════════════════════════
  //  Public API
  // ═══════════════════════════════════════════
  window.RSProduct = {
    init(config) {
      Object.assign(_config, config);
      _sectionId = _config.sectionId;
      _initialized = true;

      const boot = function () {
        _initGalleryData();

        // Initial gallery render — slides are set before _reinitGallery so
        // connectedCallback fires once with the correct variant-filtered slide set.
        if (_config.initialVariantOptions && _config.initialVariantOptions.length) {
          _renderGalleryForVariant(_config.initialVariantOptions);
          _reinitGallery();
          _activateGalleryThumb();
        }

        _captureDefaults();
        _patchProductForm();
        _guardButtonDisabled();
        _subscribeVariantChange();
        _bindCartIntercept();
        _resolveButton();
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
      } else {
        boot();
      }
    },

    get sectionId() { return _sectionId; },
    get config()    { return Object.assign({}, _config); },

    // ATC Gates
    registerGate(id, options) {
      options = options || {};
      _gates.set(id, {
        disabled: !!options.disabled,
        text: options.text || '',
        priority: options.priority || 0
      });
      if (_initialized) _scheduleResolve();
    },
    updateGate(id, updates) {
      const gate = _gates.get(id);
      if (!gate) return;
      Object.assign(gate, updates);
      if (_initialized) _scheduleResolve();
    },
    removeGate(id) {
      _gates.delete(id);
      if (_initialized) _scheduleResolve();
    },
    hasGate(id) {
      return _gates.has(id);
    },
    resolve: _resolveButton,

    // Line Item Properties
    setProperty(key, value) {
      _properties.set(key, value);
      _syncInput(key);
    },
    removeProperty(key) {
      _properties.delete(key);
      _syncInput(key);
    },
    getProperties() {
      return Object.fromEntries(_properties);
    },
    clearProperties() {
      _properties.forEach(function (_, key) { _syncInput(key); });
      _properties.clear();
    },

    // Multi-Item Submit
    registerItemsBuilder(id, builder) {
      _itemsBuilderId = id;
      _itemsBuilder = builder;
      _bindCartIntercept();
    },
    removeItemsBuilder(id) {
      if (_itemsBuilderId === id) {
        _itemsBuilderId = null;
        _itemsBuilder = null;
      }
    },
    hasItemsBuilder() {
      return _itemsBuilder !== null;
    },

    // Gallery
    gallery: {
      inject(src, index) {
        _injectedCounter++;
        const id = 'rs-injected-image-' + _injectedCounter;

        const slider = _findGallerySlider();
        if (!slider) return null;

        const slide = _buildSlideElement({ src, _id: id });
        _insertAtIndex(slider, slide, index);

        let thumbEl = null;
        const thumbsHolder = _findThumbsHolder();
        if (thumbsHolder) {
          thumbEl = _buildThumbElement({ src, _id: id });
          _insertThumbAtIndex(thumbsHolder, thumbEl, index);
        }

        _injectedSlides.set(id, { slide, thumb: thumbEl });
        _reinitGallery();
        _activateGalleryThumb();
        return id;
      },

      remove(id) {
        const entry = _injectedSlides.get(id);
        if (!entry) return;
        if (entry.slide && entry.slide.parentNode) entry.slide.remove();
        if (entry.thumb && entry.thumb.parentNode) entry.thumb.remove();
        _injectedSlides.delete(id);
        _reinitGallery();
        _activateGalleryThumb();
      },

      update(id, src) {
        const entry = _injectedSlides.get(id);
        if (!entry || !entry.slide) return;
        const img = entry.slide.querySelector('img');
        if (img) img.src = src;
        const photo = entry.slide.querySelector('[data-zoom-image]');
        if (photo) photo.setAttribute('data-image-src', src);
        if (entry.thumb) {
          const thumbImg = entry.thumb.querySelector('img');
          if (thumbImg) thumbImg.src = src;
          const thumbLink = entry.thumb.querySelector('a');
          if (thumbLink) thumbLink.href = src;
        }
      },

      navigate(id) {
        const entry = _injectedSlides.get(id);
        if (!entry || !entry.slide) return;

        // Mobile: flex-scroll gallery
        if (window.innerWidth <= 749) {
          entry.slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          return;
        }

        const mediaId = entry.slide.getAttribute('data-media-id');
        if (!mediaId) return;

        // Desktop: dispatch theme:media:select — product-images handles the rest
        const productImagesEl = entry.slide.closest('product-images');
        if (productImagesEl) {
          productImagesEl.dispatchEvent(new CustomEvent('theme:media:select', {
            detail: { id: mediaId },
          }));
        }
      }
    }
  };

  window.dispatchEvent(new CustomEvent('rs:product-core-ready'));
})();

/* ── Siblings Dropdown ───────────────────────── */

(function () {
  document.querySelectorAll('[data-rs-sibling-dropdown]').forEach(function (select) {
    select.addEventListener('change', function () {
      if (this.value) window.location.href = this.value;
    });
  });
})();

/* ── RS Modal ────────────────────────────────── */

(function () {
  var CLOSING_ATTR = 'closing';

  function openModal(dialog) {
    if (!dialog || dialog.hasAttribute('open')) return;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    dialog.removeAttribute('inert');
    dialog.setAttribute('aria-hidden', 'false');

    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));

    if (window.theme?.a11y?.trapFocus) {
      window.theme.waitForAnimationEnd?.(dialog).then(function () {
        window.theme.a11y.trapFocus(dialog);
        var target = dialog.querySelector('[autofocus]') || dialog.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (target) target.focus();
      });
    }
  }

  function closeModal(dialog) {
    if (!dialog || !dialog.hasAttribute('open')) return;
    if (dialog.hasAttribute(CLOSING_ATTR)) return;

    dialog.setAttribute(CLOSING_ATTR, '');

    var onEnd = function () {
      dialog.removeAttribute(CLOSING_ATTR);

      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }

      dialog.setAttribute('inert', '');
      dialog.setAttribute('aria-hidden', 'true');

      if (!window.theme?.hasOpenModals?.()) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      }

      if (window.theme?.a11y) {
        window.theme.a11y.removeTrapFocus();
        window.theme.a11y.autoFocusLastElement();
      }
    };

    if (window.theme?.waitForAnimationEnd) {
      window.theme.waitForAnimationEnd(dialog).then(onEnd);
    } else {
      requestAnimationFrame(function () {
        setTimeout(onEnd, 250);
      });
    }
  }

  document.addEventListener('click', function (e) {
    var openTrigger = e.target.closest('[data-rs-modal-open]');
    if (openTrigger) {
      e.preventDefault();
      var id = openTrigger.getAttribute('data-rs-modal-open');
      var dialog = document.getElementById(id);
      if (dialog) {
        if (window.theme?.a11y) window.theme.a11y.lastElement = openTrigger;
        openModal(dialog);
      }
      return;
    }

    var closeTrigger = e.target.closest('[data-rs-modal-close]');
    if (closeTrigger) {
      e.preventDefault();
      var d = closeTrigger.closest('dialog.rs-modal');
      if (d) closeModal(d);
      return;
    }

    if (e.target.nodeName === 'DIALOG' && e.target.classList.contains('rs-modal') && !e.target.hasAttribute('data-rs-modal-self-managed')) {
      closeModal(e.target);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.code !== 'Escape') return;
    var dialog = document.querySelector('dialog.rs-modal[open]:not([inert]):not([data-rs-modal-self-managed])');
    if (dialog) {
      e.preventDefault();
      closeModal(dialog);
    }
  });

  window.RSModal = { open: openModal, close: closeModal };
})();
